import { REALTIME_RECONNECTED, REALTIME_TOPIC_REVOKED } from '@/shared/realtime';
import type { ModuleSetupContext } from '@/shared/registry/types';

import { diskKeys, invalidateDisk, invalidateDiskLists } from './api';

/** Realtime event names of the disk module (backend `DiskRealtime`). */
export const DISK_EVENTS = {
  itemsCreated: 'disk.items-created',
  itemsUpdated: 'disk.items-updated',
  itemsDeleted: 'disk.items-deleted',
  itemsMoved: 'disk.items-moved',
  itemsRestored: 'disk.items-restored',
  sharesChanged: 'disk.shares-changed',
  commentCreated: 'disk.comment-created',
  commentUpdated: 'disk.comment-updated',
  commentDeleted: 'disk.comment-deleted',
} as const;

const COMMENT_EVENTS: readonly string[] = [
  DISK_EVENTS.commentCreated,
  DISK_EVENTS.commentUpdated,
  DISK_EVENTS.commentDeleted,
];

/** Payload of every `disk.*` event: what changed where (clients refetch what they show). */
export interface DiskChangedPayload {
  storageId: string;
  folderIds: string[];
  itemIds: string[];
}

/**
 * Live disk: folder pages subscribe `disk-storage:<id>` (or `disk-item:<sharedFolderId>` when opened through a share),
 * the file panel its item's topic; people whose access changed get `disk.shares-changed` on their own connection.
 * Every event refreshes the folders and items it names; moves and restores may touch any listing.
 */
export function setupDiskRealtime({ queryClient, realtime }: ModuleSetupContext): void {
  realtime.on<DiskChangedPayload | null>('disk.*', (payload, envelope) => {
    if (!payload) return;
    if (envelope.type === DISK_EVENTS.itemsMoved || envelope.type === DISK_EVENTS.itemsRestored) {
      invalidateDiskLists(queryClient);
    }
    invalidateDisk(queryClient, {
      storageId: payload.storageId,
      folderIds: payload.folderIds,
      itemIds: payload.itemIds,
      shares: envelope.type === DISK_EVENTS.sharesChanged,
    });
    // A thread changed: the open panel's comments (and the count on its tab, which `invalidateDisk` refreshed).
    if (COMMENT_EVENTS.includes(envelope.type)) {
      for (const id of payload.itemIds)
        void queryClient.invalidateQueries({ queryKey: diskKeys.comments(id) });
    }
  });
  realtime.on<{ topic?: string } | null>(REALTIME_TOPIC_REVOKED, (payload) => {
    if (payload?.topic?.startsWith('disk-')) void queryClient.invalidateQueries({ queryKey: diskKeys.all });
  });
  realtime.on(REALTIME_RECONNECTED, () => void queryClient.invalidateQueries({ queryKey: diskKeys.all }));
}
